import { Controller, useForm } from 'react-hook-form';

import type { WithFormErrors } from '@/components/Forms/FormControl';
import FormControl from '@/components/Forms/FormControl';
import FormErrors from '@/components/Forms/FormErrors';
import useFormSubmit from '@/components/Forms/useFormSubmit';
import { Button } from '@/components/react-aria/button';
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from '@/components/react-aria/dialog';
import { Label } from '@/components/react-aria/field';
import { Modal } from '@/components/react-aria/modal';
import { NumberField, NumberInput } from '@/components/react-aria/number-field';
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from '@/components/react-aria/select';
import formatReleaseLine from '@/renderer/utils/formatReleaseLine';
import { useDebounce } from '@/renderer/utils/hooks/useDebounce';
import { useRequest } from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';
import { useUpdateEffect } from '@react-aria/utils';
import { useCallback, useEffect, useState } from 'react';

// Types
type UpdateRelease = Pick<
  API.Release,
  | 'id'
  | 'closingSide'
  | 'closingTotal'
  | 'closingLineValue'
  | 'visitorScore'
  | 'homeScore'
  | 'overtimes'
  | 'result'
>;
type TUpdateReleaseForm = API.WithData<Partial<UpdateRelease>[], { doDryRun?: boolean }> & {
  // Top-level flag the server's patch/batch handler actually reads (req.body.doDryRun).
  // The generic controller defaults an ABSENT doDryRun to `true` (a safe dry-run that
  // rolls back), so grading must send doDryRun:false explicitly or nothing persists.
  doDryRun?: boolean;
};

// API requests
export const updateReleaseRequest: RequestSchema<UpdateRelease[], TUpdateReleaseForm> = {
  name: 'updateReleaseRequest',
  input: '/api/v1.1/admin/release/patch/batch',
  method: 'POST',
};

// Helper functions
// const generateResult = (release: API.Release) => {
//   const { playType, total, totalIndicator, side, sideIndicator, visitorScore, homeScore } = release;
//   const totalScore = visitorScore + homeScore;

//   if (total && playType === 'total') {
//     if (totalScore === total) {
//       return 'push';
//     }
//     if (totalIndicator === 'over') {
//       if (totalScore > total) {
//         return 'win';
//       } else if (totalScore < total) {
//         return 'loss';
//       }
//     } else if (totalIndicator === 'under') {
//       if (totalScore < total) {
//         return 'win';
//       } else if (totalScore > total) {
//         return 'loss';
//       }
//     }
//   } else if (side && playType === 'side') {
//     if (sideIndicator === 'visitor') {
//       const visitorScoreAdjusted = visitorScore + side;
//       if (visitorScoreAdjusted > homeScore) {
//         return 'win';
//       } else if (visitorScoreAdjusted < homeScore) {
//         return 'loss';
//       } else if (visitorScoreAdjusted === homeScore) {
//         return 'push';
//       }
//     } else if (sideIndicator === 'home') {
//       const homeScoreAdjusted = homeScore + side;
//       if (visitorScore > homeScoreAdjusted) {
//         return 'win';
//       } else if (visitorScore < homeScoreAdjusted) {
//         return 'loss';
//       } else if (visitorScore === homeScoreAdjusted) {
//         return 'push';
//       }
//     }
//   }
// };

// function calculateCLV(release: API.Release, values: Partial<API.Release>) {
//   const side = values.closingSide ?? release.closingSide;
//   const origSide = release.side;
//   const total = values.closingTotal ?? release.closingTotal;
//   const origTotal = release.total;
//   const totalIndicator = release.totalIndicator;

//   if (typeof origSide === 'number' && typeof side === 'number') {
//     return origSide - side;
//   }
//   if (typeof origTotal === 'number' && typeof total === 'number') {
//     if (totalIndicator === 'over') {
//       return total - origTotal;
//     } else if (totalIndicator === 'under') {
//       return origTotal - total;
//     }
//   }
//   return undefined;
// }

function Status({ className }: { className: string }) {
  return <span className={`size-2 rounded-full ${className}`} />;
}

export default function UpdateReleaseForm({
  children,
  release,
  onSuccessfulSubmit,
}: {
  children?: React.ReactNode;
  release: API.Release;
  onSuccessfulSubmit?: () => void | boolean;
}) {
  const form = useForm<WithFormErrors<TUpdateReleaseForm>>();

  useEffect(() => {
    if (release.id) {
      form.register('data.0.id');
      form.setValue('data.0.id', release.id);
    }
  }, [form, release.id]);

  const [isOpenRaw, setIsOpen] = useState(false);

  // Delay for accidental close + animations
  const isOpen = useDebounce(isOpenRaw, 10000);

  // Request hooks
  const request = useRequest(updateReleaseRequest, { manual: true });

  // Handlers
  const handleFormSubmit = useFormSubmit(
    {
      form: form,
      callback: async (body) => {
        // Fallback: ensure id is present in the payload
        if (!body.data?.[0]?.id && release.id) {
          body.data[0].id = release.id;
        }

        if (!body.data?.[0]?.id) {
          throw new Error('release.id missing');
        }

        await request.run(
          {
            data: body.data,
            // Server reads the dry-run flag from the top level (req.body.doDryRun) and
            // treats an absent value as a dry-run that rolls back. Send it explicitly so
            // a real grade actually commits; only dry-run when the toggle is set.
            doDryRun: body?.meta?.doDryRun === true,
            meta: body.meta,
          },
          { throwOnRun: true },
        );

        setTimeout(() => {
          if (body?.meta?.doDryRun) {
            document
              ?.getElementById?.(`update-release-${body.data?.[0]?.id}-dryrun`)
              ?.scrollIntoView({ block: 'start' });
          } else if (onSuccessfulSubmit) {
            if (onSuccessfulSubmit() !== false) {
              setIsOpen(false);
            }
          } else {
            setIsOpen(false);
          }
        }, 500);
      },
    },
    [
      release,
      // refetch,
      // reset
    ],
  );

  const onSelectionChange = useCallback(
    (onChange: (...event: any[]) => void, value: any) => (list: any[] | any) => {
      if (Array.isArray(list)) {
        if (Array.isArray(value) ? list.join(',') === value.join(',') : list.join(',') === value) {
          return;
        }

        return onChange(list);
      }

      if (list === '' || list === undefined || list === null) {
        if (value === '' || value === undefined || value === null) {
          return;
        }

        return Array.isArray(value) ? onChange([]) : value;
      }

      if (Array.isArray(value) ? list === value.join(',') : list === value) {
        return;
      }

      return Array.isArray(value) ? onChange([list]) : onChange(list);
    },
    [],
  );

  // const closingSide = form.watch('data.0.closingSide');
  // const closingTotal = form.watch('data.0.closingTotal');

  // useUpdateEffect(() => {
  //   const clv = calculateCLV(release, {
  //     closingSide,
  //     closingTotal,
  //   });
  //   if (typeof clv === 'number' && !isNaN(clv)) {
  //     form.setValue('data.0.closingLineValue', clv);
  //   }
  // }, [closingSide, closingTotal, release, form]);

  useUpdateEffect(() => {
    if (!isOpenRaw && !isOpen) {
      form.reset({ data: [] });
    }
  }, [isOpenRaw, isOpen]);

  return (
    <DialogTrigger onOpenChange={setIsOpen} isOpen={isOpenRaw}>
      <Button variant="unstyle" value={release.id.toString()}>
        {children || 'Edit'}
      </Button>
      {/* Pad the drawer overlay by the top/bottom safe-area insets so the grade
          form sheet clears the notch/status bar and home indicator on native
          (the fixed overlay escapes the shell's content inset) (#1601). */}
      <Modal
        drawer
        placement="right"
        isDismissable
        onOpenChange={setIsOpen}
        classNames={{
          // The keyboard's inset already covers the home indicator (iOS), so
          // the overlay stops padding it while the keyboard is up (#2142).
          modalOverlay:
            'pt-[calc(0.5rem+var(--safe-top))] pb-[calc(0.5rem+max(0px,var(--safe-bottom)-var(--kb-inset,0px)))]',
        }}
      >
        {(isOpenRaw || isOpen) && (
          <Dialog className="h-full !focus-visible:outline-none">
            <DialogHeader>{formatReleaseLine(release, false, true)}</DialogHeader>
            <DialogCloseButton />
            <div className="flex h-full flex-col divide-y divide-zinc-900/10 dark:divide-white/10">
              <DialogBody className="flex-1 pb-8">
                <form
                  onSubmit={handleFormSubmit}
                  method="POST"
                  className="space-y-6"
                  id={`update-release-${release.id}`}
                >
                  {/* <input type="hidden" {...form.register('data.0.idd', { value: release.id })} /> */}

                  <div className="mt-4 space-y-4">
                    {(release.playType === 'side' || release.playType === 'total') && (
                      <>
                        {release.playType === 'side' && (
                          <FormControl
                            form={form}
                            name="data.0.closingSide"
                            label="Closing side"
                            autoComplete="off"
                            aria-autocomplete="none"
                            wrap={NumberInput}
                            as={NumberField}
                            props={{
                              minValue: -100,
                              maxValue: 100,
                              defaultValue: release.closingSide,
                              step: 0.5,
                              name: 'data.0.closingSide',
                            }}
                            formBehavior="react-aria"
                          />
                        )}
                        {release.playType === 'total' && (
                          <FormControl
                            form={form}
                            name="data.0.closingTotal"
                            label="Closing total"
                            autoComplete="off"
                            aria-autocomplete="none"
                            wrap={NumberInput}
                            as={NumberField}
                            props={{
                              minValue: -65535,
                              maxValue: 65535,
                              defaultValue: release.closingTotal,
                              step: 0.5,
                            }}
                            formBehavior="react-aria"
                          />
                        )}
                        <FormControl
                          form={form}
                          name="data.0.closingLineValue"
                          label="CLV"
                          autoComplete="off"
                          aria-autocomplete="none"
                          wrap={NumberInput}
                          as={NumberField}
                          props={{
                            minValue: -20,
                            maxValue: 20,
                            defaultValue: release.closingLineValue,
                            step: 0.5,
                          }}
                          formBehavior="react-aria"
                        />
                        <FormControl
                          form={form}
                          name="data.0.visitorScore"
                          label={`${release.visitor?.name ?? 'Visitor'} score`}
                          autoComplete="off"
                          aria-autocomplete="none"
                          wrap={NumberInput}
                          as={NumberField}
                          props={{
                            minValue: 0,
                            defaultValue: release.visitorScore,
                            step: 1,
                          }}
                          formBehavior="react-aria"
                        />
                        <FormControl
                          form={form}
                          name="data.0.homeScore"
                          label={`${release.home?.name ?? 'Home'} score`}
                          autoComplete="off"
                          aria-autocomplete="none"
                          wrap={NumberInput}
                          as={NumberField}
                          props={{
                            minValue: 0,
                            defaultValue: release.homeScore,
                            step: 1,
                          }}
                          formBehavior="react-aria"
                        />
                        {release.period !== 1 && (
                          <FormControl
                            form={form}
                            name="data.0.overtimes"
                            label="Overtimes"
                            autoComplete="off"
                            aria-autocomplete="none"
                            wrap={NumberInput}
                            as={NumberField}
                            props={{
                              minValue: 0,
                              defaultValue: release.overtimes || 0,
                            }}
                            formBehavior="react-aria"
                          />
                        )}
                      </>
                    )}

                    {/* <Controller
                    control={form.control}
                    name="data.0.result"
                    defaultValue={release.result}
                    render={({ field: { value, ref, onChange, onBlur, name, disabled } }) => (
                      <ComboBox
                        aria-label="Result"
                        className="sm:col-span-3"
                        menuTrigger="focus"
                        name={name}
                        onSelectionChange={onSelectionChange(onChange, value)}
                        onBlur={onBlur}
                        selectedKey={value}
                        defaultSelectedKey={release.result}
                      >
                        <Label>Result</Label>
                        <ComboBoxGroup>
                          <ComboBoxInput placeholder="Result" ref={ref} />
                          <ComboBoxButton />
                        </ComboBoxGroup>

                        <ComboBoxPopover>
                          <ComboBoxListBox selectedKeys={value}>
                            <ComboBoxListItem
                              id="pending"
                              icon={<Status className="bg-amber-500" />}
                            >
                              Pending
                            </ComboBoxListItem>
                            <ComboBoxListItem id="win" icon={<Status className="bg-emerald-400" />}>
                              Win
                            </ComboBoxListItem>
                            <ComboBoxListItem id="loss" icon={<Status className="bg-rose-600" />}>
                              Loss
                            </ComboBoxListItem>
                            <ComboBoxListItem id="push" icon={<Status className="bg-zinc-300" />}>
                              Push
                            </ComboBoxListItem>
                            <ComboBoxListItem
                              id="canceled"
                              icon={<Status className="bg-zinc-300" />}
                            >
                              Canceled
                            </ComboBoxListItem>
                            <ComboBoxListItem
                              id="noAction"
                              icon={<Status className="bg-zinc-300" />}
                            >
                              No action
                            </ComboBoxListItem>
                          </ComboBoxListBox>
                        </ComboBoxPopover>
                      </ComboBox>
                    )}
                  /> */}

                    <Controller
                      control={form.control}
                      name="data.0.result"
                      defaultValue={release.result}
                      render={({ field: { value, onChange, onBlur, name } }) => (
                        <Select
                          placeholder="Select&hellip;"
                          aria-label="Result"
                          className="sm:col-span-3"
                          name={name}
                          onSelectionChange={onSelectionChange(onChange, value)}
                          onBlur={onBlur}
                          selectedKey={value}
                          defaultSelectedKey={release.result}
                        >
                          <Label>Result</Label>
                          <SelectButton></SelectButton>
                          <SelectPopover>
                            <SelectListBox>
                              <SelectListItem id="pending">
                                <Status className="bg-amber-500" />
                                Pending
                              </SelectListItem>
                              <SelectListItem id="win">
                                <Status className="bg-emerald-400" />
                                Win
                              </SelectListItem>
                              <SelectListItem id="loss">
                                <Status className="bg-rose-600" />
                                Loss
                              </SelectListItem>
                              <SelectListItem id="push">
                                <Status className="bg-zinc-300" />
                                Push
                              </SelectListItem>
                              <SelectListItem id="canceled">
                                <Status className="bg-zinc-300" />
                                Canceled
                              </SelectListItem>
                              <SelectListItem id="noAction">
                                <Status className="bg-zinc-300" />
                                No action
                              </SelectListItem>
                            </SelectListBox>
                          </SelectPopover>
                        </Select>
                      )}
                    />
                    <FormErrors
                      form={form}
                      request={request}
                      rewrite={(err) => {
                        if (err.code === 404) {
                          return <>Record not found.</>;
                        }

                        return err.toString();
                      }}
                    />
                  </div>
                </form>
                <pre id={`update-release-${release.id}-dryrun`}>
                  {form.formState.isSubmitSuccessful &&
                    form.getValues('meta.doDryRun') === true &&
                    JSON.stringify(request.data, null, 2)}
                </pre>
              </DialogBody>
              <DialogFooter className="shrink-0 justify-end">
                <DialogCloseButton className="ring-1 ring-zinc-900/10 dark:ring-white/10">
                  Not now
                </DialogCloseButton>

                {/* <Controller
                  control={form.control}
                  name="meta.doDryRun"
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      value={field.value ? '1' : undefined}
                      defaultSelected={false}
                    >
                      Dry Run
                    </Checkbox>
                  )}
                /> */}

                <Button
                  type="submit"
                  isDisabled={
                    request.loading ||
                    // !form.formState.isDirty ||
                    !form.formState.isValid
                  }
                  isPending={request.loading}
                  color="accent"
                  variant="solid"
                  form={`update-release-${release.id}`}
                >
                  Update
                </Button>
              </DialogFooter>
            </div>
          </Dialog>
        )}
      </Modal>
    </DialogTrigger>
  );
}
